import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_CONTACT_LIST_PAGE_SIZE,
  DEFAULT_CONTACT_LIST_SORT,
  type ContactDetail,
  type ContactGroupSummary,
  type ContactListItem,
  type ContactListQuery,
  type ContactListSortName,
  type ContactBulkGroupsMode,
  type CreateContactRequest,
  type UpdateContactRequest,
} from "@multi-chat/shared";
import {
  bulkDeleteContactsInGroup,
  bulkDeleteContacts,
  bulkUpdateContactGroups,
  createContact,
  deleteContact,
  getContact,
  listContactGroups,
  listContacts,
  restoreContact,
  updateContact,
} from "../../api/contacts-api.js";
import {
  buildContactsListQuery,
  contactsListQueryKey,
  contactsMatchingQueryKey,
  isContactsListQueryConstrained,
  mergeContactsListPagination,
  type ContactsFilterState,
} from "./contacts-list-query.js";
import {
  allActiveSelection,
  contactsSelectionAfterPageChange,
  contactsSelectionAfterQueryScopeChange,
  emptyContactsSelection,
  matchingQuerySelection,
  toContactBulkSelectionPayload,
  toggleContactsHeaderPageSelection,
  toggleExplicitContactSelection,
  toggleSemanticContactExclusion,
  type ContactsSelectionState,
} from "./contacts-selection.js";

export type { ContactsFilterState } from "./contacts-list-query.js";
export type { ContactsSelectionState } from "./contacts-selection.js";

export function useContacts(organizationId: number) {
  const [query, setQuery] = useState<ContactListQuery>({
    page: 1,
    pageSize: DEFAULT_CONTACT_LIST_PAGE_SIZE,
    sort: DEFAULT_CONTACT_LIST_SORT,
  });
  const [searchInput, setSearchInput] = useState("");
  const [committedSearch, setCommittedSearch] = useState<string | undefined>();
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [filters, setFiltersState] = useState<ContactsFilterState>({});
  const [groups, setGroups] = useState<ContactGroupSummary[]>([]);
  const [list, setList] = useState<ContactListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<ContactsSelectionState>(emptyContactsSelection);
  const [orgActiveCount, setOrgActiveCount] = useState(0);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ContactDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const listStateRef = useRef({
    query,
    committedSearch,
    selectedGroupId,
    filters,
  });
  listStateRef.current = { query, committedSearch, selectedGroupId, filters };

  const effectiveQuery = useMemo(
    (): ContactListQuery =>
      buildContactsListQuery({
        query,
        committedSearch,
        selectedGroupId,
        filters,
      }),
    [query, committedSearch, selectedGroupId, filters],
  );

  const listQueryKey = useMemo(
    () => contactsListQueryKey(effectiveQuery),
    [
      query.page,
      query.pageSize,
      query.sort,
      committedSearch,
      selectedGroupId,
      filters.hasEmail,
      filters.hasPhone,
      filters.platform,
      filters.createdFrom,
      filters.createdTo,
    ],
  );

  const matchingQueryKey = useMemo(
    () => contactsMatchingQueryKey(effectiveQuery),
    [
      committedSearch,
      selectedGroupId,
      filters.hasEmail,
      filters.hasPhone,
      filters.platform,
      filters.createdFrom,
      filters.createdTo,
    ],
  );

  const selectedIds = selection.mode === "ids" ? selection.ids : new Set<number>();

  const setFilters = useCallback((next: ContactsFilterState) => {
    setFiltersState(next);
    setQuery((prev) => ({ ...prev, page: 1 }));
    setSelection(contactsSelectionAfterQueryScopeChange);
  }, []);

  const selectGroup = useCallback((groupId: number | null) => {
    setSelectedGroupId(groupId);
    setQuery((prev) => ({ ...prev, page: 1 }));
    setSelection(contactsSelectionAfterQueryScopeChange);
  }, []);

  const reloadGroups = useCallback(async () => {
    const items = await listContactGroups(organizationId);
    setGroups(items);
  }, [organizationId]);

  const loadList = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    const fetchQuery = buildContactsListQuery(listStateRef.current);
    try {
      const orgCountPromise = isContactsListQueryConstrained(fetchQuery)
        ? listContacts(
            organizationId,
            { page: 1, pageSize: DEFAULT_CONTACT_LIST_PAGE_SIZE },
            controller.signal,
          )
        : null;
      const response = await listContacts(organizationId, fetchQuery, controller.signal);
      if (controller.signal.aborted) return;
      setList(response.items);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setQuery((prev) => mergeContactsListPagination(prev, response));
      if (orgCountPromise) {
        try {
          const orgCount = await orgCountPromise;
          if (!controller.signal.aborted) {
            setOrgActiveCount(orgCount.totalCount);
          }
        } catch {
          /* Keep the last known org-wide active total. */
        }
      } else {
        setOrgActiveCount(response.totalCount);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError("LOAD_FAILED");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [organizationId]);

  useEffect(() => {
    void reloadGroups();
  }, [reloadGroups]);

  useEffect(() => {
    void loadList();
  }, [listQueryKey, loadList]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCommittedSearch(searchInput.trim() || undefined);
      setQuery((prev) => ({ ...prev, page: 1 }));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSelection(contactsSelectionAfterQueryScopeChange);
  }, [matchingQueryKey]);

  const openDetail = useCallback(
    async (contactId: number) => {
      setDetailId(contactId);
      setDetailLoading(true);
      try {
        const row = await getContact(organizationId, contactId);
        setDetail(row);
      } catch {
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [organizationId],
  );

  const closeDetail = useCallback(() => {
    setDetailId(null);
    setDetail(null);
  }, []);

  const setPage = useCallback((page: number) => {
    setQuery((prev) => ({ ...prev, page }));
    setSelection(contactsSelectionAfterPageChange);
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setQuery((prev) => ({ ...prev, pageSize, page: 1 }));
    setSelection(contactsSelectionAfterPageChange);
  }, []);

  const setSort = useCallback((sort: ContactListSortName) => {
    setQuery((prev) => ({ ...prev, sort, page: 1 }));
  }, []);

  const toggleSelect = useCallback((id: number) => {
    setSelection((prev) => {
      if (prev.mode === "matching_query" || prev.mode === "all_active") {
        return toggleSemanticContactExclusion(prev, id);
      }
      return toggleExplicitContactSelection(prev.ids, id);
    });
  }, []);

  const selectPage = useCallback(() => {
    setSelection({ mode: "ids", ids: new Set(list.map((row) => Number(row.id))) });
  }, [list]);

  const toggleHeaderPageSelection = useCallback(() => {
    const visibleIds = list.map((row) => Number(row.id));
    setSelection((prev) =>
      toggleContactsHeaderPageSelection(prev, visibleIds, matchingQueryKey),
    );
  }, [list, matchingQueryKey]);

  const selectMatchingQuery = useCallback(() => {
    setSelection(matchingQuerySelection(matchingQueryKey));
  }, [matchingQueryKey]);

  const selectAllActive = useCallback(() => {
    setSelection(allActiveSelection());
  }, []);

  const clearSelection = useCallback(() => setSelection(emptyContactsSelection()), []);

  const saveContact = useCallback(
    async (contactId: number | null, body: CreateContactRequest | UpdateContactRequest) => {
      if (contactId == null) {
        await createContact(organizationId, body as CreateContactRequest);
      } else {
        await updateContact(organizationId, contactId, body as UpdateContactRequest);
      }
      await loadList();
      await reloadGroups();
      if (contactId != null) {
        await openDetail(contactId);
      }
    },
    [organizationId, loadList, reloadGroups, openDetail],
  );

  const removeContact = useCallback(
    async (contactId: number) => {
      await deleteContact(organizationId, contactId);
      closeDetail();
      await loadList();
    },
    [organizationId, closeDetail, loadList],
  );

  const restoreContactById = useCallback(
    async (contactId: number) => {
      const restored = await restoreContact(organizationId, contactId);
      setDetail(restored);
      await loadList();
    },
    [organizationId, loadList],
  );

  const bulkDeleteSelected = useCallback(async () => {
    const payload = toContactBulkSelectionPayload(selection, effectiveQuery, matchingQueryKey);
    if (!payload) return { deleted: 0 };
    const result = await bulkDeleteContacts(organizationId, { selection: payload });
    closeDetail();
    setSelection(emptyContactsSelection());
    await loadList();
    await reloadGroups();
    return result;
  }, [
    selection,
    effectiveQuery,
    matchingQueryKey,
    organizationId,
    closeDetail,
    loadList,
    reloadGroups,
  ]);

  const bulkUpdateGroups = useCallback(
    async (
      mode: ContactBulkGroupsMode,
      groupSelection: { groupIds: number[]; newGroupNames: string[] },
    ) => {
      const payload = toContactBulkSelectionPayload(selection, effectiveQuery, matchingQueryKey);
      if (!payload) return { updated: 0 };
      const result = await bulkUpdateContactGroups(organizationId, {
        selection: payload,
        mode,
        groupIds: groupSelection.groupIds.length > 0 ? groupSelection.groupIds : undefined,
        newGroupNames:
          groupSelection.newGroupNames.length > 0 ? groupSelection.newGroupNames : undefined,
      });
      setSelection(emptyContactsSelection());
      await loadList();
      await reloadGroups();
      if (detailId != null) {
        await openDetail(detailId);
      }
      return result;
    },
    [
      selection,
      effectiveQuery,
      matchingQueryKey,
      organizationId,
      detailId,
      loadList,
      reloadGroups,
      openDetail,
    ],
  );

  const bulkDeleteAllInGroup = useCallback(
    async (groupId: number) => {
      const result = await bulkDeleteContactsInGroup(organizationId, groupId);
      closeDetail();
      setSelection(emptyContactsSelection());
      await loadList();
      await reloadGroups();
      return result;
    },
    [organizationId, closeDetail, loadList, reloadGroups],
  );

  return {
    query,
    effectiveQuery,
    matchingQueryKey,
    searchInput,
    setSearchInput,
    selectedGroupId,
    setSelectedGroupId: selectGroup,
    filters,
    setFilters,
    groups,
    reloadGroups,
    list,
    totalCount,
    orgActiveCount,
    totalPages,
    loading,
    error,
    reloadList: loadList,
    selection,
    selectedIds,
    toggleSelect,
    selectPage,
    toggleHeaderPageSelection,
    selectMatchingQuery,
    selectAllActive,
    clearSelection,
    setPage,
    setPageSize,
    setSort,
    detailId,
    detail,
    detailLoading,
    openDetail,
    closeDetail,
    saveContact,
    removeContact,
    restoreContactById,
    bulkDeleteSelected,
    bulkUpdateGroups,
    bulkDeleteAllInGroup,
  };
}
